/**
 * main.tsx -- Dashboard application entry point (T368 / INT-405).
 *
 * Mounts AppShell inside the WorkOS AuthKit + Convex provider stack on the
 * #root DOM element: <AuthKitProvider> → <ConvexProviderWithAuth> → <AppShell>.
 * These sit OUTSIDE AppShell so auth state is available to AuthGate (the first
 * meaningful provider inside the shell). The Convex client is bridged to WorkOS
 * via useWorkOSConvexAuth (see ./lib/workosConvexAuth).
 *
 * Global stylesheet import order is load-bearing:
 *   1. tokens.css      -- CSS custom-property declarations (Cohere 2026 token vocabulary)
 *   2. animations.css  -- Legacy keyframe stub (retained for import compatibility)
 *   3. motion.css      -- Central motion stylesheet (20 keyframes + utility classes)
 *   4. responsive.css  -- Six-band responsive layer + display-switching classes (FR-004/FR-005)
 *   5. components.css  -- Component-scoped rules (skip-link, TopNav active underline, etc.)
 *
 * Sentry init is a side-effect import and must run before any React rendering
 * so the error boundary can capture early lifecycle errors. The init module
 * was relocated from the legacy repo-root `src/sentry.ts` to
 * `apps/dashboard/src/sentry.ts` when the legacy `src/` tree was decommissioned.
 */

// -- Side-effect: Sentry init (must precede React rendering) --
import "./sentry";

// -- Global stylesheets (order matters) --
import "./styles/tokens.css";
import "./styles/animations.css";
import "./styles/motion.css";
import "./styles/responsive.css";
import "./styles/components.css";

import { createRoot } from "react-dom/client";
import { ConvexReactClient, ConvexProviderWithAuth } from "convex/react";
import { AuthKitProvider } from "@workos-inc/authkit-react";
import { AppShell } from "./AppShell";
import { useWorkOSConvexAuth } from "./lib/workosConvexAuth";
import { EnsureUserOnLogin } from "./lib/EnsureUserOnLogin";

// -- Convex client --
const convex = new ConvexReactClient(
  import.meta.env.VITE_CONVEX_URL as string,
);

// -- WorkOS AuthKit (frontend) config --
// VITE_WORKOS_CLIENT_ID is required; VITE_WORKOS_REDIRECT_URI is optional
// (AuthKit derives a sensible default when omitted).
const workosClientId = import.meta.env.VITE_WORKOS_CLIENT_ID as string;
const workosRedirectUri = import.meta.env.VITE_WORKOS_REDIRECT_URI as
  | string
  | undefined;

// AuthKit `devMode` stores the refresh token in localStorage instead of
// memory, so silent refresh survives on a deployed origin without a
// same-site AuthKit cookie. authkit-js auto-enables it only on localhost;
// opt in explicitly for preview deploys (e.g. capi-1) that point at the
// staging WorkOS env via VITE_WORKOS_DEV_MODE=true. Left undefined elsewhere
// so localhost auto-detect and production (cookie-based) behaviour are
// unchanged. Tradeoff: refresh token becomes XSS-readable — acceptable for a
// staging preview; the production-correct fix is a custom AuthKit domain.
const workosDevMode =
  import.meta.env.VITE_WORKOS_DEV_MODE === "true" ? true : undefined;

// Treat an unset value or the .env.local placeholder as "not configured".
const workosConfigured =
  !!workosClientId && !workosClientId.startsWith("client_REPLACE");

// After WorkOS completes the code exchange it invokes this callback. WorkOS
// redirects to the real path `/auth/callback`, but this app is a hash-router
// SPA — so move into the hash app (role-aware Today dispatcher) and drop the
// callback path + `?code` query. Without this the app strands on /auth/callback.
function onWorkosRedirectCallback(): void {
  try {
    // Clear the /auth/callback path and the leftover ?code query.
    window.history.replaceState(null, "", "/");
  } catch {
    // Older browsers — fall back to hash-only navigation below.
  }
  // Setting the hash fires `hashchange`, which the router subscribes to.
  window.location.hash = "#/today";
}

// -- Mount --
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error(
    "Root element #root not found. Ensure index.html contains <div id=\"root\"></div>.",
  );
}

// Guard: without a WorkOS Client ID, AuthKit's createClient throws
// (NoClientIdProvidedException) and useAuth().isLoading never resolves, leaving
// the app stuck on an infinite "Loading…" spinner. Fail loudly with an
// actionable message instead — set VITE_WORKOS_CLIENT_ID in .env.local.
if (!workosConfigured) {
  createRoot(rootElement).render(
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "var(--font-ui, system-ui, sans-serif)",
        color: "var(--color-surface-sumi, #1A1614)",
        background: "var(--color-surface-shiro, #FAF2EA)",
      }}
    >
      <div style={{ maxWidth: 520, lineHeight: 1.5 }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 8px" }}>
          Authentication is not configured
        </h1>
        <p style={{ margin: "0 0 12px", opacity: 0.8 }}>
          <code>VITE_WORKOS_CLIENT_ID</code> is not set, so the WorkOS sign-in
          client can’t initialize. Add it to <code>.env.local</code> and restart
          the dev server:
        </p>
        <pre
          style={{
            background: "var(--color-surface-kinari, #F0E6D6)",
            padding: "12px 14px",
            borderRadius: 8,
            overflowX: "auto",
            fontSize: 13,
          }}
        >
{`VITE_WORKOS_CLIENT_ID=client_...
VITE_WORKOS_REDIRECT_URI=http://localhost:5176/auth/callback`}
        </pre>
      </div>
    </div>,
  );
} else {
  // NOTE: no <React.StrictMode> here. In dev, StrictMode double-mounts
  // AuthKitProvider, so the one-time WorkOS `?code` is exchanged twice — the
  // second attempt has no matching PKCE/state and WorkOS rejects it
  // ("authorization_code … did not originate at the application"), leaving the
  // session unset and bouncing back to the login page. Mounting once fixes it.
  createRoot(rootElement).render(
    <AuthKitProvider
      clientId={workosClientId}
      redirectUri={workosRedirectUri}
      devMode={workosDevMode}
      onRedirectCallback={onWorkosRedirectCallback}
    >
      <ConvexProviderWithAuth client={convex} useAuth={useWorkOSConvexAuth}>
        <EnsureUserOnLogin>
          <AppShell />
        </EnsureUserOnLogin>
      </ConvexProviderWithAuth>
    </AuthKitProvider>,
  );
}

// ---------------------------------------------------------------------------
// Service worker — conditional registration behind ff:service-worker-enabled
// ---------------------------------------------------------------------------

if (
  "serviceWorker" in navigator &&
  (() => {
    try {
      return localStorage.getItem("ff:service-worker-enabled") === "true";
    } catch {
      return false;
    }
  })()
) {
  navigator.serviceWorker.register("/sw.js").catch((err) => {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[sw] Service worker registration failed:", err);
    }
  });
}
